import { lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { LoadingOverlay } from '@airtm/airtm-ui';
import { CUSTODIAL_PROVIDERS } from '@constants';
import useMe from 'utils/hooks/useMe/useMe';

const ActivityRoute = lazy(() => import('screens/Activity/ActivityRoute'));
const AirnerRoute = lazy(() => import('screens/Airners/AirnerRoute'));
const Dashboard = lazy(() => import('screens/Dashboard/Dashboard'));
const Fund = lazy(() => import('screens/Fund/BridgeFund/BridgeFund'));
const Withdraw = lazy(() => import('screens/Withdraw/Withdraw'));
const Payout = lazy(() => import('screens/Payout/Payout'));
const PayinDetail = lazy(() => import('screens/Payin/PayinDetail'));
const Settings = lazy(() => import('screens/Settings/Settings'));
const StatementDownload = lazy(() => import('screens/StatementDownload/StatementDownload'));
const Operation = lazy(() => import('screens/Operation/Operation'));

const Routes = function () {
  const { me, custodialProvider } = useMe({
    include: { enterprise: true },
    fetchPolicy: 'cache-first',
  });

  const {
    canAddFunds,
    canAccessWithdraw,
    canCreateSinglePayment,
    canCreateBulkPayment,
    canApproveBulkPayment,
    canCancelBulkPayment,
    canAccessAirners,
  } = me?.organizationPermissions ?? {};

  const addFundsIsVisible = canAddFunds && !!custodialProvider;
  const allowWithdrawPage = custodialProvider === CUSTODIAL_PROVIDERS.BRIDGE && canAccessWithdraw;
  const showAirnersFeature = canAccessAirners && me?.enterprise?.activeAirnerFeature;

  const allowPayoutFeature = [
    canCreateSinglePayment,
    canCreateBulkPayment,
    canApproveBulkPayment,
    canCancelBulkPayment,
  ].some(Boolean);

  return (
    <Suspense fallback={<LoadingOverlay />}>
      <Switch>
        <Route exact path="/" render={() => <Redirect to="/home" />} />
        <Route path="/home" component={Dashboard} />
        {allowWithdrawPage && <Route path="/withdraw" component={Withdraw} />}
        {addFundsIsVisible && <Route path="/fund" component={Fund} />}
        {allowPayoutFeature && <Route path="/payout" component={Payout} />}
        <Route path="/payin/:id" component={PayinDetail} />
        <Route path="/activity" component={ActivityRoute} />
        <Route path="/settings" component={Settings} />
        <Route path="/statements/:id" component={StatementDownload} />
        <Route path="/operations/:id" component={Operation} />
        {showAirnersFeature && <Route path="/airner-intelligence" component={AirnerRoute} />}
        <Redirect to="/home" />
      </Switch>
    </Suspense>
  );
};

export default Routes;
